import React from "@platform/react";

import { Sequential } from "@platform/utils/dist/uuid";
import { Component, getClassName } from "..";
import { useResizeHandler } from "../../Hook/resizeHandler";
import type { SizeResizeHandlerHook } from "../../Hook/resizeHandler";
import Rules from "./_rules.module.scss";
import type { PropTypesCarousel } from "./types";
import {
    ERROR_CAROUSELPLATFORM,
    NAME_CONT_CAROUSELPLATFORM,
    TYPE_CONT_CAROUSELPLATFORM,
    DEF_CAROUSELPLATFORM,
    DIR_CAROUSELPLATFORM,
    NAME_CAROUSELPLATFORM,
    TYPE_CAROUSELPLATFORM,
} from "./types";

const INDEX = { FIRST: 0, NOT_FOUND: -1, LAST: -1 } as const;
const COUNT = { NOD: 0, ONE: 1, TWO: 2 } as const;

export function Carousel(props: PropTypesCarousel) {
    const { children, direction, duration, ...rest } = props;

    const ref = React.useRef<HTMLDivElement>();
    const refRoot = React.useRef<HTMLElement>();
    const [clones, setClones] = React.useState<React.ReactNode[]>([]);
    const [size, setSize] = React.useState<SizeResizeHandlerHook | null>(null);
    const [isReady, setReady] = React.useState(false);

    // const memoWindowResizeHandler = React.useCallback(handleWindowResize, []);
    const memoParentResizeHandler = React.useCallback(handleParentResize, []);
    const memoChildrenParser = React.useCallback(handleChildrenParse, [children]);
    const isHorizontal = ["ltr", "rtl"].includes(direction);
    const className = getClassName(rest.className, ...DIR_CAROUSELPLATFORM[direction]);

    // whenever the parentElement changes size
    useResizeHandler({
        target: ref.current?.parentElement,
        handler: memoParentResizeHandler,
    });

    // Clone original children.
    React.useEffect(() => {
        const _children = memoChildrenParser();
        if (_children) setClones(_children);
    }, [memoChildrenParser]);

    // Determine if original children have to be cloned in order to fill the available space.
    React.useEffect(() => {
        setReady(false);
        const { current } = ref;
        if (!current || !size) return;
        const origs: HTMLElement[] = Array.from(current.querySelectorAll(`[data-orig]`));
        if (!origs.length) return;
        // the space that has to be filled (at least double of what's available)
        const spaceFillable = (isHorizontal ? size.width : size.height) * COUNT.TWO;
        const _children = memoChildrenParser();
        if (!_children) throw new Error(ERROR_CAROUSELPLATFORM.NO_CHILDREN);
        // start cloning elements until filling parent's space.
        let index: number = INDEX.FIRST;
        let space = COUNT.NOD;
        while (space <= spaceFillable) {
            if (!origs[index]) index = INDEX.FIRST;
            space += isHorizontal ? origs[index].offsetWidth : origs[index].offsetHeight;
            const clone = React.cloneElement(_children[index], {
                ..._children[index].props,
                key: `clone-${Sequential()}`,
                "data-orig": undefined,
                "data-clone": _children[index].props["data-orig"],
            });
            _children.push(clone);
            index++;
        }
        setClones(_children);
        setReady(true);
    }, [isHorizontal, memoChildrenParser, size]);

    // Observe all clones, move them to the beginning or end of the list to maintain the appearance
    // eslint-disable-next-line complexity
    React.useEffect(() => {
        const { current } = ref;
        const { current: root } = refRoot;
        // are there clones to consider?
        let nodeBase: HTMLElement | undefined = undefined;
        let nodeTarget: HTMLElement | undefined = undefined;
        if (!root || !current || !isReady) return handleUnmount;
        const nodes = Array.from(current.children) as HTMLElement[];
        if (direction === "ltr" || direction === "ttb") nodeBase = nodes[nodes.length + INDEX.LAST];
        if (direction === "rtl" || direction === "btt") nodeBase = nodes[INDEX.FIRST];
        if (!nodeBase || !(nodeBase instanceof HTMLElement)) return handleUnmount;
        nodeBase.dataset.base = "";
        // Determine the index of the middle element.
        nodeTarget = nodes.find((node) => {
            if (!nodeBase) return false;
            // make sure the element is beyond half
            const isBeyondHalf = isHorizontal
                ? node.offsetLeft > root.clientWidth
                : node.offsetTop > root.clientHeight;

            if (!isBeyondHalf) return false;
            const idBase = nodeBase.dataset.orig || nodeBase.dataset.clone;
            const idNode = node.dataset.orig || node.dataset.clone;
            return idBase === idNode;
        });
        if (!nodeTarget) return handleUnmount;
        nodeTarget.dataset.target = "";
        // Determine animation vars
        const vars: Partial<
            Record<"time" | "xOrig" | "xDest" | "yOrig" | "yDest", string | number | undefined>
        > = { time: `${duration}s` };

        if (isHorizontal) {
            vars.yDest = 0;
            vars.yOrig = 0;
        } else {
            vars.xOrig = 0;
            vars.xDest = 0;
        }
        // TODO: this still needs some testing.
        switch (direction) {
            case "ltr":
                vars.xOrig = -Math.abs(current.scrollWidth) + root.clientWidth;
                vars.xDest = 0;
                break;
            case "rtl":
                vars.xOrig = 0;
                vars.xDest = -Math.abs(nodeTarget.offsetLeft);
                break;
            case "ttb":
                vars.yOrig = -Math.abs(current.scrollHeight) + root.clientHeight;
                vars.yDest = vars.yOrig + root.clientHeight + nodeBase.offsetHeight;
                break;
            case "btt":
                vars.yOrig = 0;
                vars.yDest = -Math.abs(nodeTarget.offsetTop) - nodeTarget.offsetHeight;
                break;
        }
        Object.entries(vars).forEach(([key, val]) => {
            current.style.setProperty(`--${key}`, typeof val === "string" ? val : `${val}px`);
        });

        current.classList.add(Rules.animate);

        return handleUnmount;

        function handleUnmount() {
            if (!current) return;
            if (nodeBase) nodeBase.removeAttribute("data-base");
            if (nodeTarget) nodeTarget.removeAttribute("data-target");
            current.classList.remove(Rules.animate);
            current.style.removeProperty("transform");
        }
    }, [direction, duration, isReady, clones, isHorizontal]);

    return (
        <Component
            {...rest}
            ref={handleRef.bind(null, refRoot)}
            className={className}
            name={NAME_CAROUSELPLATFORM}
            type={TYPE_CAROUSELPLATFORM}>
            <aside>
                <div
                    ref={handleRef.bind(null, ref)}
                    data-name={NAME_CONT_CAROUSELPLATFORM}
                    data-type={TYPE_CONT_CAROUSELPLATFORM}>
                    {clones}
                </div>
            </aside>
        </Component>
    );

    function handleRef<T>(target: React.MutableRefObject<T>, el: T | null) {
        Object.defineProperty(target, "current", { value: el });
    }

    function handleParentResize(s: SizeResizeHandlerHook) {
        setSize(s);
    }

    function handleChildrenParse() {
        const { current: cont } = ref;
        if (!cont) return null;
        const _clones = React.Children.map(children, (child, i) => {
            if (!React.isValidElement(child)) return null;
            const clone = React.cloneElement(child, {
                key: child.key ? child.key : `orig-${i}`,
                "data-orig": i,
            });
            return clone;
        });
        if (!_clones || !_clones.length) return null;
        return _clones.filter(Boolean);
    }
}
Carousel.defaultProps = DEF_CAROUSELPLATFORM;
